/**
 * SuperAdminMenuItems — the founder-tool rows of the account menu: Support
 * Console (/super-admin) and Command Center (the Access-gated JARVIS HUD).
 *
 * Moved here from the Sidebar's standalone super-admin block at Brian's
 * request (2026-07-19): these live in the profile menu beside Notifications /
 * Dark mode / Change Password. One shared component because the v1
 * (GlobalControls) and v2 (SidebarAccountZone) menus must stay item-for-item
 * in sync until the chrome flag flips — a single home cannot drift.
 *
 * Renders nothing for non-super-admins. The "jarvis" easter egg
 * (CommandCenterEasterEgg) remains the keyboard route to the same HUD.
 */
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/useAuthStore';
import { COMMAND_CENTER_URL } from '../../lib/commandCenter';

interface Props {
  /** Close the parent account menu (navigation rows only). */
  onClose: () => void;
}

export default function SuperAdminMenuItems({ onClose }: Props) {
  const isSuperAdmin = useAuthStore(s => s.isSuperAdmin);
  const navigate = useNavigate();
  if (!isSuperAdmin()) return null;

  const openCommandCenter = () => {
    onClose();
    // No 'noopener' so we can detect a blocked pop-up and tell the user,
    // rather than failing silently. The destination is our own Access-gated
    // HUD, so reverse-tabnabbing isn't a concern here.
    const win = window.open(COMMAND_CENTER_URL, '_blank');
    if (!win) {
      toast.error('Command Center couldn’t open — allow pop-ups for this site, then try again.');
    }
  };

  return (
    <>
      <hr className="my-1" />
      <button
        onClick={() => { onClose(); navigate('/super-admin'); }}
        title="Support Console — platform-level support and impersonation tools"
        className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
      >
        🛡️ Support Console
      </button>
      <button
        onClick={openCommandCenter}
        title="Avium Command Center (JARVIS) — opens in a new tab"
        className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
      >
        ◉ Command Center
      </button>
    </>
  );
}
